import type { PluginEngineLoginUser } from '~/types/blog'
import { STORAGE_KEYS } from './storage'

const TOKEN_STORAGE_KEY = STORAGE_KEYS.AUTH_TOKEN
const USER_ID_STORAGE_KEY = STORAGE_KEYS.AUTH_USER_ID
const EXPIRES_AT_STORAGE_KEY = STORAGE_KEYS.AUTH_EXPIRES_AT
const USER_STORAGE_KEY = STORAGE_KEYS.AUTH_USER
export const PLUGIN_AUTH_CHANGED_EVENT = 'plugin-auth-changed'

// normalizeExpiresAt 将秒或毫秒时间戳统一转换为毫秒。
const normalizeExpiresAt = (value: string | number) => {
  const timestamp = Number(value)
  if (!Number.isFinite(timestamp) || timestamp <= 0) {
    return ''
  }
  return timestamp < 1_000_000_000_000 ? timestamp * 1000 : timestamp
}

// readStorage 安全读取浏览器登录缓存。
const readStorage = (key: string): string => {
  if (!import.meta.client) {
    return ''
  }
  try {
    return String(window.localStorage.getItem(key) || '').trim()
  } catch {
    return ''
  }
}

// writeStorage 安全写入浏览器登录缓存。
const writeStorage = (key: string, value: string | number | null | undefined) => {
  if (!import.meta.client) {
    return
  }
  try {
    if (value === undefined || value === null || String(value).trim() === '') {
      window.localStorage.removeItem(key)
      return
    }
    window.localStorage.setItem(key, String(value).trim())
  } catch {
    // 隐私模式或存储空间异常时保持页面可用。
  }
}

// dispatchPluginAuthChanged 通知前台同步插件互联登录状态。
const dispatchPluginAuthChanged = () => {
  if (import.meta.client) {
    window.dispatchEvent(new Event(PLUGIN_AUTH_CHANGED_EVENT))
  }
}

// getPluginAuthToken 返回插件互联登录令牌。
export const getPluginAuthToken = () => readStorage(TOKEN_STORAGE_KEY)

// getPluginAuthUserID 返回插件互联用户 ID。
export const getPluginAuthUserID = () => readStorage(USER_ID_STORAGE_KEY)

// getPluginAuthExpiresAt 返回统一为毫秒时间戳的会话过期时间。
export const getPluginAuthExpiresAt = () => {
  return Number(normalizeExpiresAt(readStorage(EXPIRES_AT_STORAGE_KEY)) || 0)
}

// hasValidPluginAuthSession 判断本地插件互联会话是否仍然有效。
export const hasValidPluginAuthSession = () => Boolean(
  getPluginAuthToken() && getPluginAuthUserID() && getPluginAuthExpiresAt() > Date.now()
)

// getPluginAuthUser 返回缓存的插件互联用户资料。
export const getPluginAuthUser = (): PluginEngineLoginUser | null => {
  const text = readStorage(USER_STORAGE_KEY)
  if (!text) {
    return null
  }
  try {
    return JSON.parse(text) as PluginEngineLoginUser
  } catch {
    return null
  }
}

// setPluginAuthSession 保存插件互联登录会话。
export const setPluginAuthSession = (token: string, expiresAt: string | number, user: PluginEngineLoginUser) => {
  writeStorage(TOKEN_STORAGE_KEY, token)
  writeStorage(USER_ID_STORAGE_KEY, user.id)
  writeStorage(EXPIRES_AT_STORAGE_KEY, normalizeExpiresAt(expiresAt))
  writeStorage(USER_STORAGE_KEY, JSON.stringify(user))
  dispatchPluginAuthChanged()
}

// refreshPluginAuthSession 保存 JWT 中间件续期后的令牌与过期时间。
export const refreshPluginAuthSession = (token: string, expiresAt: string | number) => {
  writeStorage(TOKEN_STORAGE_KEY, token)
  writeStorage(EXPIRES_AT_STORAGE_KEY, normalizeExpiresAt(expiresAt))
}

// clearPluginAuthSession 清除插件互联登录会话。
export const clearPluginAuthSession = () => {
  writeStorage(TOKEN_STORAGE_KEY, '')
  writeStorage(USER_ID_STORAGE_KEY, '')
  writeStorage(EXPIRES_AT_STORAGE_KEY, '')
  writeStorage(USER_STORAGE_KEY, '')
  dispatchPluginAuthChanged()
}
